import { useEffect, useState } from "react";
import RightModal from "../components/templates/RightModal";
import { getSupplierOrdersList, getSupplierOrderDetails, resetOrderDetails, selectSupplierOrdersData, selectSupplierOrdersLoading } from "../redux/supplierOrders/supplierOrdersSlice";
import DynamicIcon from "../components/DynamicIcon";
import SupplierOrderLine from "../components/supplierOrdersPage/SupplierOrderLine";
import ListPagination from "../components/templates/ListPagination";
import LoadingSection from "../components/LoadingSection";
import { selectAccountData } from "../redux/main/accountSlice";
import { resetRefreshNow, selectRightModalId, selectRightModalIsOpen, selectRightModalIsRefreshNow, selectRightModalmode, setModal } from "../redux/rightModalSlice";
import { useDispatch, useSelector } from "react-redux";
import DynamicTitle from "../components/DynamicTitle";
import AddEditSupplierOrder from "../components/supplierOrdersPage/AddEditSupplierOrder";
import ViewSupplierOrder from "../components/supplierOrdersPage/ViewSupplierOrder";

export default function SupplierOrdersList() {
    const dispatch = useDispatch();
    const ordersList = useSelector(selectSupplierOrdersData);
    const ordersLoading = useSelector(selectSupplierOrdersLoading);
    const accountData = useSelector(selectAccountData);
    const token = localStorage.getItem('installamelo-auth-token');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    // RightModal gestita tramite Redux
    const rightModalIsOpen = useSelector(selectRightModalIsOpen);
    const rightModalMode = useSelector(selectRightModalmode);
    const rightModalOrderId = useSelector(selectRightModalId);

    const statuses = [
        { value: 'bozza', label: 'Bozza' },
        { value: 'confermato', label: 'Confermato' },
        { value: 'in_consegna', label: 'In Consegna' },
        { value: 'consegnato', label: 'Consegnato' }
    ];

    // Funzione che verrà passata al componente ListPagination
    const handlePageUpdate = (start, length) => {
        dispatch(getSupplierOrdersList({
            accountId: accountData?._id,
            token,
            search: searchTerm.length >= 3 ? searchTerm : '',
            start,
            length,
            statusId: statusFilter
        }));
        dispatch(resetRefreshNow());
    };

    // Eseguo la chiamata iniziale quando il componente viene montato o quando cambiano i filtri
    useEffect(() => {
        if (accountData?._id) {
            dispatch(getSupplierOrdersList({
                accountId: accountData._id,
                token,
                search: searchTerm.length >= 3 ? searchTerm : '',
                start: 0,
                length: 10,
                statusId: statusFilter
            }));
            dispatch(resetRefreshNow());
        }
    }, [dispatch, accountData, searchTerm, statusFilter]);

    // Carica i dettagli dell'ordine quando il modale viene aperto per modifica o visualizzazione
    useEffect(() => {
        if (rightModalIsOpen && rightModalOrderId && (rightModalMode === 'edit' || rightModalMode === 'view')) {
            dispatch(getSupplierOrderDetails({
                supplierOrderId: rightModalOrderId,
                token
            }));
        }
    }, [rightModalIsOpen, rightModalOrderId, rightModalMode, dispatch, token]);

    // Reset dei dettagli ordine quando si chiude il modale
    useEffect(() => {
        if (!rightModalIsOpen) {
            dispatch(resetOrderDetails());
        }
    }, [rightModalIsOpen, dispatch]);

    // Avvio il refresh dei dati quando isRefreshNow della RightModal si attiva
    const rightModalIsRefreshNow = useSelector(selectRightModalIsRefreshNow);
    useEffect(() => {
        if (rightModalIsRefreshNow) handlePageUpdate(0, 10);
    }, [rightModalIsRefreshNow]);

    return (
        <div className="page">
            {ordersLoading && <LoadingSection isFixed />}
            <DynamicTitle title="Ordini Fornitore" />

            <h1>Ordini Fornitore</h1>
            <div className="box">
                {/* Barra di ricerca e filtri */}
                <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
                    <div className="relative w-96">
                        <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
                            <DynamicIcon iconClass="fa-solid fa-search" />
                        </div>
                        <input
                            type="text"
                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full ps-10 p-2.5"
                            placeholder="Cerca ordine..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                            <button
                                className="absolute inset-y-0 end-0 flex items-center pe-3"
                                onClick={() => setSearchTerm('')}
                            >
                                <DynamicIcon iconClass="fa-solid fa-times" />
                            </button>
                        )}
                    </div>

                    {/* Filtri */}
                    <div className="flex gap-2">
                        {/* Filtro per Stato */}
                        <select
                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2.5"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="">Tutti gli stati</option>
                            {statuses.map((status) => (
                                <option key={status.value} value={status.value}>
                                    {status.label}
                                </option>
                            ))}
                        </select>

                        {/* Bottone Nuovo Ordine */}
                        <button
                            className="primary"
                            onClick={() => dispatch(setModal({ mode: 'add' }))}
                        >
                            <DynamicIcon iconClass="fa-solid fa-plus" />
                            Nuovo Ordine
                        </button>
                    </div>
                </div>

                <ListPagination
                    initTotalElements={ordersList?.recordsFiltered || 0}
                    onPageChange={handlePageUpdate}
                >
                    {/* Tabella ordini */}
                    <div className="w-full text-sm flex flex-col gap-2">
                        {/* Headers */}
                        <div className="w-full flex justify-between font-bold">
                            <div className="px-6 py-4 w-1/6">CODICE</div>
                            <div className="px-6 py-4 w-1/5">FORNITORE</div>
                            <div className="px-6 py-4 w-1/6">DATA ORDINE</div>
                            <div className="px-6 py-4 w-1/6">STATO</div>
                            <div className="px-6 py-4 w-1/6">DATA CONSEGNA</div>
                            <div className="px-6 py-4 w-1/6 flex justify-end">AZIONI</div>
                        </div>

                        {/* Rows */}
                        {
                            ordersList?.data?.map((order, index) => (
                                <SupplierOrderLine
                                    key={order.id}
                                    orderData={order}
                                    index={index}
                                />
                            ))
                        }

                        {/* Messaggio quando non ci sono dati */}
                        {(!ordersList?.data || ordersList.data.length === 0) && !ordersLoading && (
                            <div className="w-full py-4 text-center text-gray-500" key="no-data-message">
                                Nessun ordine fornitore trovato
                            </div>
                        )}
                    </div>
                </ListPagination>
            </div>

            {/* RIGHT MODAL per inserimento o modifica ordine */}
            {
                rightModalIsOpen && (
                    <RightModal>
                        {(rightModalMode === 'add' || rightModalMode === 'edit') && (
                            <AddEditSupplierOrder mode={rightModalMode} />
                        )}
                        {
                            rightModalMode === 'view' && rightModalOrderId && (
                                <ViewSupplierOrder />
                            )
                        }
                    </RightModal>
                )
            }
        </div>
    );
}
